import { createSignal, onMount } from "solid-js";
import { clientRepo } from "~/server/apis/client_repo";
import {
  BrandExtraMetadata,
  SimulatedTransactionDetails,
  VoucherProduct,
} from "~/server/types/brand";
import {
  defaultRequestForADenominationsBrand,
  defaultRequestForAVariantBrand,
} from "~/utils/webstore/default_simulate_request";

export function useHomepageSingleBrandData(props: {
  brandKey: string;
  clientId: string;
  mode: "hubble" | "rnr" | "sdk";
}) {
  const { brandKey, clientId, mode } = props;

  // Loading and data states
  const [isLoading, setIsLoading] = createSignal(true);
  const [error, setError] = createSignal<string | null>(null);
  const [brand, setBrand] = createSignal<VoucherProduct | null>(null);
  const [brandExtraMetadata, setBrandExtraMetadata] =
    createSignal<BrandExtraMetadata | null>(null);
  const [simulateTransactionDetails, setSimulateTransactionDetails] =
    createSignal<SimulatedTransactionDetails | null>(null);
  const [simulateError, setSimulateError] = createSignal<string | null>(null);

  // Fetch all data on mount
  onMount(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch brand and metadata in parallel
      const brandPromise = clientRepo.getBrand(brandKey);
      const metadataPromise = clientRepo.getBrandExtraMetadata(brandKey);

      const [brandData, metadataData] = await Promise.all([
        brandPromise,
        metadataPromise,
      ]);

      setBrand(brandData);
      setBrandExtraMetadata(metadataData);

      // Now simulate with the brand data
      await simulateTransaction(brandData);
    } catch (err: any) {
      console.error("Error fetching homepage single brand data:", err);
      setError(err?.message ?? "Failed to load brand data");
    } finally {
      setIsLoading(false);
    }
  });

  // Simulate transaction
  async function simulateTransaction(brandData: VoucherProduct) {
    try {
      const isVariantBrand = brandData.variantDetailedResponse != null;
      const isDenominationsBrand =
        brandData.amountConditions.denominations != null;

      let simulateRequest: any = {
        voucherProductId: brandData.externalId,
        amount: brandData.amountConditions.defaultAmount,
        consumeCoins: mode === "rnr",
      };

      if (isVariantBrand) {
        simulateRequest = {
          ...simulateRequest,
          ...defaultRequestForAVariantBrand(brandData),
        };
      } else if (isDenominationsBrand) {
        simulateRequest = {
          ...simulateRequest,
          ...defaultRequestForADenominationsBrand(brandData),
        };
      }

      const simulateResponse = await clientRepo.simulate(simulateRequest);
      const filteredData = simulateResponse.data.filter(
        (data) => data.paymentRoute != "WALLET"
      )[0];

      setSimulateTransactionDetails(filteredData);
    } catch (err: any) {
      console.error("Error simulating transaction:", err);
      setSimulateError(err?.message ?? "Failed to simulate transaction");
    }
  }

  return {
    isLoading,
    error,
    brand,
    brandExtraMetadata,
    simulateTransactionDetails,
    simulateError,
  };
}
